/**
 * The routes this tenant owns, in one place: a path is written in the route, in
 * the `menuPath` its guard checks and in the menu entry, and those must agree.
 *
 * RA2N's own paths are not here — they come from the `/me/permissions` menu
 * tree, and this bundle cannot change them.
 *
 * Stored with the leading slash, the form `menuPath` and `<Link>` want;
 * `routes.tsx` takes `.slice(1)` where the router wants them relative.
 */

/** ダッシュ — the tenant's landing screen, `clinicIndex` at `/{tenant}`. */
export const KEILOG_DASHBOARD_PATH = '/';

/** 利用者管理 — 利用者一覧 / 同意書一覧 / 撮影受領キュー on one screen. */
export const KEILOG_PATIENT_MANAGEMENT_PATH = '/patient-management';

/**
 * Query parameter of {@link KEILOG_PATIENT_MANAGEMENT_PATH} `#riyou`: the 利用者番号 whose 利用者詳細
 * opens on 利用者一覧 (SCR-088-006 訪問データチェック). Its own key, not 同意書一覧's `patient`, so
 * it never narrows or opens anything on that tab.
 */
export const KEILOG_RIYOU_PATIENT_QUERY_KEY = 'riyou_patient';

/**
 * 同意書管理 (SCR-050) — its own keilog path, NOT RA2N's `/consents/treatment_period`.
 *
 * Core mounts that path with a static import (src/router/TenantRouter.tsx:292), so a
 * componentOverrides entry is never read, and a same-path entry in `routes.tsx` loses because
 * customize routes are spread after core's (TenantRouter.tsx:521). Same approach as 利用者管理:
 * a tenant path the menu points at. Must match Config/rbac.php and screenRegistry.ts character
 * for character — a mismatch drops the menu entry without any error.
 */
export const KEILOG_CONSENT_MANAGEMENT_PATH = '/consent-management';

/**
 * 同意書詳細編集 — CORE's screen (`src/router/TenantRouter.tsx:314`), which 📝 同意書登録 hands
 * over to the moment it has saved: its button says 「登録して詳細編集」 and the remaining columns
 * live there.
 *
 * Core mounts it RELATIVE to `/{tenant}`, so the result has to go through
 * `keilogTenantScopedPath`. An unscoped `/consents/…` is read as a SITE segment by the root
 * router, which bounced the browser to `/consents/login` and threw the operator out of the
 * screen right after a successful save (measured 2026-09-23, 同意書 #334303).
 */
export const keilogConsentEditPath = (patientId: number, consentId: number): string =>
  `/consents/${patientId}/edit/${consentId}`;

/**
 * 保険証確認・一覧 (SCR-053) — the 保険証 of every 利用者 in scope, and the panel that confirms
 * one.
 *
 * Tenant-owned: RA2N has no counterpart. Its nearest labels, 保険証・同意書入力
 * (`patients/edit`) and 保険証読取 (`patients/certification`), are per-利用者 form screens and
 * answer a different question. Must match `app/Tenants/Keilog/Config/rbac.php` and
 * `services/screenRegistry.ts` character for character — a mismatch drops the menu entry with
 * no error at all.
 */
export const KEILOG_INSURANCE_REVIEW_PATH = '/insurance-review';

/**
 * 施術報告書 (SCR-084) — 対象利用者 and the report form on one screen.
 *
 * Singular, so it cannot collide with RA2N's own per-patient routes under
 * `treatment-reports/:patientId/…`, which stay core's and answer a different
 * question (the reports of one 利用者, reached from that 利用者's screen).
 */
export const KEILOG_TREATMENT_REPORT_PATH = '/treatment-report';

/**
 * 施術経過報告書 PC (SCR-084-003) — the 📈 tab beside 施術報告書, the 施術家's
 * screen only (FIX-PLAN Q-E).
 *
 * Tenant-owned and singular, like {@link KEILOG_TREATMENT_REPORT_PATH}: RA2N
 * has no screen here, and the phone's 施術経過報告書 (SCR-084-002) is a tab
 * inside `/treatment-report`, not a path of its own. Must match
 * `app/Tenants/Keilog/Config/rbac.php` and `services/screenRegistry.ts`
 * character for character, or the rail entry is dropped without an error.
 */
export const KEILOG_PROGRESS_REPORT_PC_PATH = '/progress-report';

/**
 * 施術報告書一覧 (SCR-086) — the aggregate list of 同意書 with the state of their
 * 施術報告書, across every 利用者 in scope.
 *
 * Tenant-owned: RA2N's own `treatment-reports/{patientId}/list` is a child
 * screen of one 利用者 and answers a different question, so this is a new path
 * rather than an override.
 */
export const KEILOG_TREATMENT_REPORT_LIST_PATH = '/treatment_reports/list';

/**
 * 書類セット (SCR-137-001) — one set of documents per 確定 rezept, bound in the
 * order its 提出先 takes them. Tenant-owned: RA2N has no such screen.
 */
export const KEILOG_DOCUMENT_SET_PATH = '/document-set';

/**
 * 総括票印刷 (SCR-133), menu 鍼灸按マ印刷 — the month's issued rezepts aggregated per 保険者,
 * and the 総括票 / 請求書 printed for them. Tenant-owned: RA2N prints invoices for patients only.
 * Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_INSURER_INVOICE_PATH = '/insurer-invoices';

/**
 * レセプト印刷 (SCR-136) — 本部管理 prints the month's rezepts of every 自店登録 拠点 as one set,
 * 拠点 by 拠点. Tenant-owned: RA2N's レセプト発行 (`rezepts/init`) prints one 治療院 at a time.
 * Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_RECEIPT_PRINT_PATH = '/receipt-print';

/**
 * 全拠点 総括票・レセプト提出状況 (SCR-093), menu label 全拠点状況 — 本部管理 and 本部 watch how far
 * every 拠点's claims of a month have got: made, checked, printed, sent, returned, reviewed.
 * Tenant-owned. Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_RECEIPT_STATUS_PATH = '/receipt-status';

/**
 * 実績締め管理 (SCR-082, Result closing management) — 本部管理 and 本部 lock each 拠点 × 対象月 before
 * レセプト are printed, and change the 締め期限 (closing deadline). Tenant-owned (BA decisions No.13).
 * Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_RECEIPT_DEADLINE_PATH = '/receipt-deadline';

/**
 * レセプト状況 (SCR-089 / BD SCR-402, Receipt status) — 本部管理 follows every レセプト of the tenant once
 * printed: papers arrived at 本部, 受領到着日, the 本部 status axes and the 不備内容. Tenant-owned; the concept
 * file is `本部管理/receipt-list.html`. Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_RECEIPT_LIST_PATH = '/receipt-list';

/**
 * 月間締め明細 (SCR-404, Monthly closing statement): FCオーナー (FC owner) reads how its 拠点 (locations)
 * closed each month (`#shime`) and the issued レセプト (claims) of one closing month (`#meisai`).
 * Tenant-owned, RA2N has no such screen. Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_MONTHLY_CLOSING_PATH = '/monthly-closing';

/**
 * 月次確定 (SCR-403, monthly confirmation) — 本部管理 watches every 拠点 (location) × 対象月 × サービス:
 * errors, warnings, 同意期間前・未判定 visits and how far its レセプト went. Under 予定実績 › 実績.
 * Tenant-owned, taken by no RA2N route. Must match `app/Tenants/Keilog/Config/rbac.php` character for
 * character.
 */
export const KEILOG_MONTHLY_CONFIRM_PATH = '/monthly-confirm';

/** SCR-123 FB出力(全銀): 本部管理 › 帳票出力 › データ出力. Must match app/Tenants/Keilog/Config/rbac.php. */
export const KEILOG_FB_OUTPUT_PATH = '/fb-output';

/**
 * SCR-024 口座設定 (Account settings): 利用者引落口座 (User debit account), 振込依頼先口座 (Requester
 * account) and 金融機関マスタ (Bank master) as three tabs of one screen, `#user` / `#payee` / `#bank`
 * (`FCオーナー/fb-accounts.html`). A tenant path: RAH has no account of a 利用者 (User).
 */
export const KEILOG_FB_ACCOUNTS_PATH = '/fb-accounts';

/**
 * SCR-224 to SCR-227 ファームバンキング (Firm banking): 振替データ管理 (Transfer data management), 振替データチェック
 * (Transfer data check), 振替依頼ファイル作成 (Transfer request file creation) and 振替結果ファイル取込 (Transfer result
 * file import) as four tabs of one screen, `#data` / `#check` / `#make` / `#recv` (`FCオーナー/firm-banking.html`).
 * A tenant path. Must match app/Tenants/Keilog/Config/rbac.php character for character.
 */
export const KEILOG_FIRM_BANKING_PATH = '/firm-banking';

/**
 * レセプト処理 (SCR-088) — スタッフ / FCオーナー lock, issue and 確定 a month's claims of their 拠点.
 * Tenant-owned: RA2N's レセプト一覧 (`rezepts/init`) stays core's and is not overridden (FIX-PLAN Q-A);
 * no KEiLOG role is sent there any more (HIT-15107). Must match `app/Tenants/Keilog/Config/rbac.php`
 * (`rezept-list`) character for character.
 */
export const KEILOG_REZEPT_PROCESSING_PATH = '/rezept-processing';

/**
 * システム設定 › 利用者登録ルート (SCR-SET1) — which 利用者登録担当 (Patient registration owner) runs the
 * 自店ルート (Own-store route). Tenant-owned and 本部管理者 only: RA2N has no screen for it. Sits under
 * `/settings` beside RBAC権限管理 (`/settings/permissions`).
 * Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_PATIENT_REGISTRATION_ROUTE_PATH = '/settings/patient-registration-route';

/**
 * 訪問スケジュール (SCR-065) — one 施術師's visits for one day, in route order.
 *
 * Tenant-owned rather than an override of RA2N's `/visits/schedule`: the screen
 * there is RA2N's week calendar, the counterpart of SCR-061 スケジュール管理, and
 * it keeps that path. Taking it over would also mean opening a `ModuleLoader`
 * seam in shared routing for a screen that is not its replacement.
 */
export const KEILOG_VISIT_SCHEDULE_PATH = '/visit-schedule';

/**
 * お知らせ (SCR-192) — the 施術師 phone app's 🔔 tab, `施術師/mobile/P6-SP07`.
 * Phone only: there is no desktop design of it and no other role's.
 */
export const KEILOG_NOTICE_PATH = '/notices';

/**
 * 施術記録入力 (SCR-067-001): the screen of one visit's record, and the menu's
 * way in to it.
 *
 * ConceptDesigns/施術師/shell/rah-shell.js draws 施術記録 › 施術記録入力 as an entry of
 * its own, and `visit-record.html` opened with no `?v=` shows the first visit of
 * the day. SCR-067-001 is a screen of its own (D-001, 2026-09-22): this path
 * alone opens the day's first visit and replaces itself with
 * `/visit-record/{visit key}`, the address 施術日報's 記録入力 opens directly.
 * Registered independently with its own SCREEN.VISIT_RECORD grant.
 */
export const KEILOG_VISIT_RECORD_ENTRY_PATH = '/visit-record';

/**
 * 施術日報 (SCR-072) — the 施術師's day as a worklist, and the screen the concept
 * opens 施術記録入力 from (`ConceptDesigns/施術師/daily-report.html`).
 *
 * Tenant-owned: RA2N has no daily report of its own. It has an endpoint of its
 * own (`/portal/{site}/daily-report`), guarded by SCREEN.DAILY_REPORT.
 */
export const KEILOG_DAILY_REPORT_PATH = '/daily-report';

/**
 * 実績管理 (SCR-073-001) — 実績確認 / 実績一覧 / 実績集計 of one month on one screen.
 *
 * Beside RA2N's `/visits/schedule` and `/visits/patient_init`, the other two entries of 予定実績,
 * and taken by no RA2N route (SPEC S4 #34). RA2N's own check result,
 * `rezepts/visit_check_ready/…`, stays where it is (S4 #27).
 */

export const KEILOG_RESULTS_MANAGEMENT_PATH = '/visits/results';

/**
 * 実績変更管理 (SCR-074) — the requests to unlock a locked レセプト, under 予定実績 › 実績 after 施術録管理
 * (BD 「Access」). Tenant-owned, beside 実績管理's `/visits/results`, taken by no RA2N route. Must
 * match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_RESULT_CHANGE_PATH = '/visits/result-changes';

/**
 * 実績差異一覧 (SCR-077) — the visits whose papers do not match, as the last 再チェック saw them, under
 * 予定実績 › 実績. Tenant-owned, taken by no RA2N route. Must match `app/Tenants/Keilog/Config/rbac.php`
 * character for character.
 */
export const KEILOG_RESULT_VARIANCE_PATH = '/visits/result-variances';

/**
 * 施術録管理 (SCR-071) — a KEiLOG path of its own, like 実績変更管理: RA2N's `/treatment_records/init`
 * (施術録検索) stays core's and is not overridden. Must match `app/Tenants/Keilog/Config/rbac.php`
 * character for character.
 */
export const KEILOG_TREATMENT_RECORD_PATH = '/treatment-records';

/**
 * タックシール印刷 (SCR-138) — a KEiLOG path of its own: RA2N has only the label button of
 * 患者情報検索 (`search-patients/print-label`), not a screen. Must match
 * `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_TACK_SEAL_PATH = '/tack-seal';

/**
 * The address BD final SCR-138 「Cách access」 gives the screen (`/{tenant}/tack-seal-print`). The menu
 * path above stays, pinned by `Config/rbac*.php` and `tests/Feature/Rbac/KeilogTackSealApiTest.php`,
 * and redirects here (`router/TackSealMenuRedirect.tsx`).
 */
export const KEILOG_TACK_SEAL_PRINT_PATH = '/tack-seal-print';

/**
 * 施術人数・売上合計 (SCR-078) — one month's 施術人数, 施術回数 and money per 利用者 / 施術種別 / 日. Under
 * 予定実績 › 実績 of the one menu every role shares (`app/Tenants/Keilog/Config/rbac.php`). Tenant-owned,
 * beside 実績管理's `/visits/results`, taken by no RA2N route. Must match that config character for
 * character.
 */
export const KEILOG_RESULTS_SUMMARY_PATH = '/visits/results-summary';

/**
 * スケジュール管理 › 週間表示 (SCR-061-002). RA2N's own `/visits/schedule`: the route stays core's and
 * the screen behind it is KEiLOG's (`router/componentOverrides.tsx`), as on 利用者別. The two longer
 * shapes RA2N hands out, `{massagerId}/{visitdate}` and `{massagerId}/{clinicLocationId}/{visitdate}`,
 * are read by the same screen.
 */
export const KEILOG_SCHEDULE_WEEKLY_PATH = '/visits/schedule';

/**
 * スケジュール管理 › トータル (SCR-061-003). Tenant-owned: RA2N has no screen of one day across every
 * 施術者. Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_SCHEDULE_TOTAL_PATH = '/visits/total';

/**
 * 領収書・請求書 (SCR-135). Case 3: RA2N has the screen at `rezepts/invoices_init`, but it reads one
 * service line per request and its page is a static import, so KEiLOG's lives here. Must match
 * `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_RECEIPT_MANAGEMENT_PATH = '/receipt-management';
/**
 * 保険証・同意書の撮影 (施術師 / `P6-SP09`) for one 利用者. Under 利用者管理's own
 * path so `resolveKeilogMenuPath` keeps it behind that menu's permission, and
 * with the id in the URL so a reload still knows whose documents these are.
 */
export const KEILOG_PATIENT_CAPTURE_PATH = `${KEILOG_PATIENT_MANAGEMENT_PATH}/capture`;

export const keilogPatientCapturePath = (patientId: number | string): string =>
  `${KEILOG_PATIENT_CAPTURE_PATH}/${patientId}`;

/**
 * 利用者情報 (施術師 / `P6-SP08`) — ONE screen: the search box on top, the card of
 * the 利用者 picked in it below.
 *
 * Until 2026-09-24 this was the second half of a pair — a list screen, a tap, a
 * card at `…/detail/:patientId`. The pair is gone: there is no list screen to be
 * opened from, so the id moved off the path segment and into a query parameter
 * (`?patientId=`), which is the form that also has to mean 「no-one picked yet」.
 * A missing segment would make the route itself optional and let
 * `…/detail/anything` match; a missing query parameter is just an empty value,
 * and {@link KEILOG_PATIENT_DETAIL_QUERY_PARAM} is the only thing that reads it.
 *
 * The id stays in the URL for the reason it always did: a reload, a Back, or a
 * link shared between 施術師 opens the same person (Common `L7.3`). Under
 * 利用者管理's own path for the reason 撮影 is — `resolveKeilogMenuPath` keeps it
 * behind that menu's permission.
 */
export const KEILOG_PATIENT_DETAIL_PATH = `${KEILOG_PATIENT_MANAGEMENT_PATH}/detail`;

/**
 * Name of the query parameter carrying the 利用者 the card draws.
 *
 * Exported rather than spelled out at each call site so the router, the page and
 * the search panel cannot drift apart on it — `usePatientDetailTarget` reads
 * exactly this key.
 */
export const KEILOG_PATIENT_DETAIL_QUERY_PARAM = 'patientId';

/**
 * The 利用者情報 URL. Called with no id it is the screen's own entry point, which
 * opens on the first 利用者 the server returns.
 */
export const keilogPatientDetailPath = (patientId?: number | string): string =>
  patientId === undefined || patientId === '' || patientId === null
    ? KEILOG_PATIENT_DETAIL_PATH
    : `${KEILOG_PATIENT_DETAIL_PATH}?${KEILOG_PATIENT_DETAIL_QUERY_PARAM}=${encodeURIComponent(String(patientId))}`;

/**
 * 申請の承認 (`委託先/approval-detail.html`) — 本部 / 受託先 register and approve
 * what 施術師 sent. A menu destination of its own, emitted for `admin` only.
 */
export const KEILOG_PATIENT_APPROVAL_PATH = '/patient-approvals';

/** 権限設定 — tenant-wide RBAC administration. */
export const KEILOG_RBAC_SETTINGS_PATH = '/settings/permissions';

/** ユーザアカウント (SCR-009) — every login of the tenant on one list. */
export const KEILOG_USER_MASTER_PATH = '/user-master';

/** ユーザアカウント 新規登録 (SCR-010), reached from the list's 新規ユーザ button. */
export const KEILOG_USER_MASTER_CREATE_PATH = `${KEILOG_USER_MASTER_PATH}/create`;

export const keilogUserMasterEditPath = (
  type: 'admin' | 'clinic_user',
  id: number
): string => `${KEILOG_USER_MASTER_PATH}/edit/${type}/${id}`;

/**
 * 拠点マスタ — 拠点一覧 with its inline 拠点詳細, plus the 新規/編集 forms under it.
 *
 * Tenant-owned rather than an override of RA2N's `/clinic_locations/init`:
 * that route renders its screen directly, with no `ModuleLoader` seam for a
 * tenant to take over, and adding one would mean changing shared routing.
 */
export const KEILOG_CLINIC_LOCATION_PATH = '/clinic-location-master';

/**
 * 保険者マスタ — SCR-014. RA2N reserved `insurers/init` for this screen in
 * `src/router/TenantRouter.tsx` (the entry is commented out there, so the path
 * is free) and every other master keeps the same `…/init` shape.
 */
export const KEILOG_INSURER_MASTER_PATH = '/insurers/init';

/**
 * 保険者作成 / 保険者編集 — the P1 registration form (BA 3-40 基本情報登録機能),
 * reached under the list's own path so `resolveKeilogMenuPath` keeps both
 * behind 保険者一覧's menu permission.
 */
export const KEILOG_INSURER_CREATE_PATH = `${KEILOG_INSURER_MASTER_PATH}/create`;

export const keilogInsurerEditPath = (id: number | string): string =>
  `${KEILOG_INSURER_MASTER_PATH}/${id}/edit`;

/**
 * TEMPORARY (demo): landing spot for menu categories the ConceptDesign has but
 * RA2N has no screen for. They all point under this placeholder prefix, which
 * renders `NotImplementedPage`; the category id is only a suffix, so the rail
 * can keep its highlight on the icon that was clicked.
 *
 * Delete this, its route in `routes.tsx`, and the branch in `conceptMenu.ts`
 * once those screens exist.
 */
export const KEILOG_COMING_SOON_PATH = '/coming-soon';

export const keilogComingSoonPath = (categoryId: string): string =>
  `${KEILOG_COMING_SOON_PATH}/${categoryId}`;

/**
 * Absolute URL for one of the paths above. `KEILOG_DASHBOARD_PATH` is `/`, so
 * the root case is handled here rather than by every caller.
 */
export const keilogTenantRootPath = (tenant: string): string => `/${tenant}`;

export const keilogTenantScopedPath = (tenant: string, path: string): string =>
  path === KEILOG_DASHBOARD_PATH
    ? keilogTenantRootPath(tenant)
    : `${keilogTenantRootPath(tenant)}${path}`;

/**
 * ログイン and パスワード再設定. Core owns the routes, but Keilog owns the
 * ログイン screen behind the first and links to the second. Functions because
 * the site segment comes from the URL — nobody is signed in yet.
 */
export const keilogLoginPath = (site: string): string => `/${site}/login`;
export const keilogForgotPasswordPath = (site: string): string => `/${site}/login?mode=forgot`;

/**
 * The retired 法人請求書作成 (SCR-128-001) list. No screen any more: the route redirects to
 * KEILOG_CORPORATE_INVOICE_DETAIL_PATH so old links still land on the detail.
 */
export const KEILOG_CORPORATE_INVOICE_PATH = '/corporate-invoices';

/**
 * 法人請求書発行（詳細） (SCR-129) — the 入金精算 › 🧾 法人請求詳細 menu entry, with
 * `?bill=<法人請求コード>&ym=<YYYYMM>`. Must match `app/Tenants/Keilog/Config/rbac.php` character for
 * character.
 */
export const KEILOG_CORPORATE_INVOICE_DETAIL_PATH = '/corporate-invoices/detail';

/**
 * 分割弁済管理 (SCR-125) — 本部's installment plans for a 基幹店. A tenant path: RAH has no installment
 * plan. Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_INSTALLMENT_PAYMENT_PATH = '/installment-payments';

/**
 * 基幹店請求書 (本部管理) / 基幹店請求詳細 (本部) (SCR-127) — 本部's monthly 精算書 per 基幹店 × 請求月. A tenant
 * path: RAH has no franchise invoice. Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_FRANCHISE_INVOICE_PATH = '/franchise-invoices';

/**
 * 保険者別入金 (SCR-113-001) — 本部管理's 入金 and 消込 per 保険者 × 申請区分 and month, and 入金状況 (SCR-113-003),
 * FCオーナー's money received per 保険者, on the same URL: RBAC v2 makes them one row, 「保険者別入金（オーナー: 入金状況）」,
 * whose menu entry of each role opens this path (`app/Tenants/Keilog/Config/rbac_menu_v2.php`). The screen drawn
 * follows the active role, see `router/PaymentStatusRoute.tsx`. A tenant path: RAH has no 入金 screen.
 */
export const KEILOG_INSURER_PAYMENT_PATH = '/insurer-payments';

/**
 * 入金差異 (SCR-115) — 本部管理's list of 入金差異 (payment variances). A tenant path: RAH has no 入金 screen.
 * Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_PAYMENT_VARIANCE_PATH = '/payment-variances';

/**
 * 拠点別入金 (SCR-402) — 本部管理 / 本部's 入金 by 拠点, レセプト and 利用者 (FIX-PLAN Q-H). A tenant path: RAH has no
 * 入金 screen. Must match `app/Tenants/Keilog/Config/rbac.php` character for character.
 */
export const KEILOG_LOCATION_PAYMENT_PATH = '/location-payments';

/**
 * 法人マスタ管理（keilog_corporations） — SCR-029. A path RA2N does not have. SCR-030 法人 新規登録
 * opens its drawer at `<path>/create`. Must match `app/Tenants/Keilog/Config/rbac.php`.
 */
export const KEILOG_CORPORATE_MASTER_PATH = '/corporate-master';

/**
 * 法人施設マスタ — SCR-031, the 🏗 法人施設 tab of 法人マスタ. No menu entry of its own (BD 管理 #1): it
 * follows the grant of the `corporate-master` row (`KEILOG_SCREEN_ROWS`). The drawers open in place.
 */
export const KEILOG_CORPORATE_FACILITY_MASTER_PATH = '/corporate-facility-master';

/** 事業所データ管理（clinic_informations） — SCR-005. */
export const KEILOG_OFFICE_DATA_PATH = '/office-data';

/**
 * 問診票 — SCR-056. The BD names the path, and RA2N has no route there, so it is
 * the tenant's own rather than an override of a core screen.
 */
export const KEILOG_QUESTIONNAIRE_PATH = '/patients/questionnaires';

/**
 * 施術継続理由・状態記入書 (SCR-060) — the 月16回受診者 grid and its detail panel.
 *
 * Follows RAH's `<resource>/init` convention. It cannot collide with the core
 * `long_term_frequents/:id/...` routes because those always carry at least three
 * segments. The menu entry lives in the 利用者 category as 継続理由・頻回計画, declared in
 * `Config/rbac.php`, `screenRegistry.ts` and `conceptMenu.ts` — all three are required.
 */
export const KEILOG_LONG_TERM_FREQUENTS_PATH = '/long_term_frequents/init';

/**
 * 転帰登録 (Outcome registration, SCR-221) — the 転帰 of every レセプト (Rezept) in scope, one row per
 * 施術年月 × 利用者 × 施術種別 × 本・関.
 *
 * Tenant-owned, and singular to match the file the concept is drawn in. RA2N has no path here: its
 * 転帰 is a MODAL on レセプト発行 (`rezepts/init`), which opens one レセプト of one month of one 治療院
 * and runs for マッサージ only. BA also removed the 転帰編集 button from the KEiLOG build of
 * レセプト一覧 (`ConceptDesigns/_spec/_spec-レセプト一覧-設計.md:131`), so this is the one way in.
 *
 * Must match `app/Tenants/Keilog/Config/rbac.php` and `services/screenRegistry.ts` character for
 * character — a mismatch drops the menu entry with no error at all.
 */
export const KEILOG_OUTCOME_REGISTRATION_PATH = '/outcome-registration';

/**
 * 売上分析 (SCR-148) — the seven-tab sales report of 本部管理 and 本部.
 *
 * A tenant path: RAH's nearest screen is 統計一覧表 (`/rezepts/statistical_init`),
 * one 治療院 for one month, which has no seam for a cross-治療院 range. The tab in
 * view is kept in the hash (`#tab0`…`#tab6`) exactly as the prototype does
 * (`ConceptDesigns/本部管理/sales-analysis.html:406-410, 467`).
 *
 * Declared in all three places a KEiLOG screen has to be: `Config/rbac.php`,
 * `screenRegistry.ts` and `conceptMenu.ts`.
 */
export const KEILOG_SALES_ANALYSIS_PATH = '/sales-analysis';

/**
 * 売上予測実績値表 (Sales forecast vs actual table) (SCR-149): one 対象月 (Target month), one row per 拠点 (Location), target against actual and forecast.
 *
 * A tenant path: RAH has no screen of this kind (BD/scope/SCR-149/sales-forecast.md B3).
 * Declared in all three places a KEiLOG screen has to be: `Config/rbac.php`,
 * `screenRegistry.ts` and `conceptMenu.ts`.
 */
export const KEILOG_SALES_FORECAST_PATH = '/sales-forecast';

/**
 * 基幹店売上 (Franchise sales) (SCR-150-001): one 対象月 (Target month), one row per 拠点 (Location) that earned, against the
 * month before. 統計分析 (Statistics analysis) › 基幹店売上 for 本部 (HQ) (`ConceptDesigns/本部/shell/rah-shell.js`).
 *
 * A tenant path: RAH has no screen of this kind (BD SCR-150-001 v1.1 Overview). No parameter.
 * Declared in all three places a KEiLOG screen has to be: `Config/rbac.php`,
 * `screenRegistry.ts` and `conceptMenu.ts`.
 */
export const KEILOG_FRANCHISE_SALES_PATH = '/franchise-sales';

/**
 * 売上日計表 (Daily sales) (SCR-151): one month, one row per day × 拠点 (Location), for 本部管理 (HQ admin) and 本部 (HQ).
 *
 * A tenant path, the concept's file name (`ConceptDesigns/本部管理/franchise-daily-sales.html`, BA A10).
 * Declared in all three places a KEiLOG screen has to be: `Config/rbac.php`,
 * `screenRegistry.ts` and `conceptMenu.ts`.
 */
export const KEILOG_DAILY_SALES_PATH = '/franchise-daily-sales';

/**
 * 実働利用者数推移表 (SCR-152) — 拠点 × five months of 利用者 with a 施術, for 本部.
 *
 * Only 本部 opens it from the menu (`ConceptDesigns/本部/shell/rah-shell.js:89-91`,
 * label 実働者推移); 本部管理 reads the same figures as a card of レポート総覧.
 *
 * Declared in all three places a KEiLOG screen has to be: `Config/rbac.php`,
 * `screenRegistry.ts` and `conceptMenu.ts`.
 */
export const KEILOG_ACTIVE_USER_TREND_PATH = '/active-user-trend';

/**
 * レポート総覧 (SCR-152) — the KPI（全拠点） card for 本部管理 and 本部, plus for 本部管理 the
 * 実働利用者数推移（拠点別） card (`ConceptDesigns/本部管理/report-dashboard.html`). First item of
 * 統計分析 in both shells (`本部管理/shell/rah-shell.js:85`, `本部/shell/rah-shell.js:63`).
 *
 * Declared in all three places a KEiLOG screen has to be: `Config/rbac.php`,
 * `screenRegistry.ts` and `conceptMenu.ts`.
 */
export const KEILOG_REPORT_DASHBOARD_PATH = '/report-dashboard';
